import { isIOSAppCheck, isAndroidAppCheck, isAndroidBridgeAvailable, postEventMessageToIOS, postEventMessageToAndroid, removeRejectedReasonViewed, isIOSAppParentChildUnsupportedVersion } from '@vue3/utils/common'
import RegistrationStore from '@vue3/store/registration-store'
import CommonStore from '@vue3/store/common-store.js'
import AuthStore from '@vue3/store/auth-store.js'
import RewardsStore from '@vue3/store/rewards-store.js'
import { mapActions, mapState } from 'pinia'
import { app } from '@vue3/configs'
import { pages } from '@vue3/configs'
import tracker from '@vue3/constants/tracker'
import { currencyFormatter } from '@vue3/utils/formatter'
import constants from '@vue3/constants'

const { COMPANY_SECTION } = tracker.component
const { SETTINGS } = tracker.label
const { PARENT } = constants.registration.companyCategory
const { TNC_TYPES } = constants

export default {
  name: 'CommonMixin',
  computed: {
    ...mapState(RewardsStore, ['engagementInfo']),
    ...mapState(CommonStore, ['isMobile', 'isTablet']),
    ...mapState(AuthStore, ['appConfigs', 'getUserEmailId', 'getRegistrationId', 'userDetails', 'currentCompanyInfo', 'tenantList', 'currentTenantId', 'isNotificationPermissionGranted']),
    isIOSApp () {
      return isIOSAppCheck
    },
    isAndroidApp () {
      return isAndroidAppCheck && isAndroidBridgeAvailable()
    },
    isParentChildFeatureEnabled () {
      return this.isIOSApp ? !isIOSAppParentChildUnsupportedVersion : true
    },
    showParentBadge () {
      return this.currentCompanyInfo.companyCategory === PARENT && this.isParentChildFeatureEnabled
    },
    isUserHasMultipleCompany () {
      return this.tenantList?.some(item => item?.companyList.length > 1)
    },
    isUserHasMultipleTenant () {
      return this.tenantList?.length > 1
    },
    isCompanySwitchAccesible () {
      return this.isParentChildFeatureEnabled && (this.isUserHasMultipleTenant || this.isUserHasMultipleCompany) && this.$route.name !== app.routerName.profile
    },
    activePoints () {
      return this.engagementInfo?.memberLoyaltyResponse?.activePoint || 0
    },
    formattedActivePoints () {
      return this.thousandSeparator(this.activePoints)
    },
    isDesktop () {
      return !this.isMobile && !this.isTablet
    },
    homePageTicker () {
      return this.appConfigs?.homePageTicker || {}
    },
    isHomePageRoute () {
      return this.$route.name === app.routerName.homePage
    },
    isScanPageRoute () {
      return this.$route.name === app.routerName.rewards
    },
    isProfilePageRoute () {
      return this.$route.name === app.routerName.profile
    },
    companyListBasedOnCurrentTenant () {
      return this.tenantList?.find(item => item.tenantId === this.currentTenantId)?.companyList || []
    },
    isUserMappedWithSingleTenant () {
      return this.tenantList.length === 1
    },
    getUnreadNotificationCount () {
      const unreadNotifications = this.engagementInfo?.unreadNotifications
      return unreadNotifications > 99 ? '99+' : unreadNotifications
    }
  },
  methods: {
    ...mapActions(CommonStore, ['setAppLoader', 'setShowTenantPickerModal']),
    ...mapActions(AuthStore, ['logoutUser', 'getUserInfo']),
    ...mapActions(RegistrationStore, ['setIsUserAlreadyVisitedReason']),
    postMessageToIOS: postEventMessageToIOS,
    postMessageToAndroid: postEventMessageToAndroid,
    currencyFormatter,
    handleBack () {
      if (window.history.state?.back) {
        this.$router.go(-1)
      } else {
        this.$router.push('/home')
      }
    },
    showToast (status, text, duration = 3000) {
      this.$toast.open({
        status,
        text,
        duration
      })
    },
    handleClearUserData () {
      this.setAppLoader(true)
      this.logoutUser({
        success: this.logoutApiSuccess
      })
    },
    logoutApiSuccess () {
      this.setIsUserAlreadyVisitedReason(false)
      this.setAppLoader(false)
      removeRejectedReasonViewed(this.currentTenantId)
      if (!this.isIOSApp) {
        return
      }
      this.postMessageToIOSListener('LOGGED_OUT')
    },
    deepCopy (obj) {
      return JSON.parse(JSON.stringify(obj))
    },
    postMessageToIOSListener (message) {
      window.webkit?.messageHandlers?.iosListener?.postMessage(message)
    },
    postMessageToIOSLoginListener (message) {
      window.webkit?.messageHandlers?.iosLoginListener?.postMessage(message)
    },
    postMessageToIOSDownloadListener (downloadInfo) {
      window.webkit?.messageHandlers?.iosDownloadListener?.postMessage(downloadInfo)
    },
    downloadFileForAndroid (base64Data, fileName, mimeType) {
      const helperUrl = `${window.location.origin}/#fileName=${encodeURIComponent(fileName)}&mimeType=${encodeURIComponent(mimeType)}&data=${encodeURIComponent(base64Data)}`
      this.postMessageToAndroid({
        openbrowser: helperUrl
      })
    },
    // Opens the download helper page instead of the file url itself, so the app shells land on
    // the same "download started" screen they get for base64 downloads
    downloadFileFromLink (fileUrl, fileName) {
      const helperUrl = `${window.location.origin}/#fileName=${encodeURIComponent(fileName || '')}&fileUrl=${encodeURIComponent(fileUrl)}`
      if (this.isIOSApp) {
        this.postMessageToIOS('openExternalLinkListener', { link: helperUrl })
        return
      }
      this.postMessageToAndroid({
        openbrowser: helperUrl
      })
    },
    thousandSeparator (value) {
      // currency formatter to support BigInt
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    viewTickerURL (url, isDocument, target = '_blank') {
      if (isDocument) {
        if (this.isIOSApp) {
          this.postMessageToIOS('iosTermsAndConditionsListener', { termsAndConditionsURL: url })
        } else {
          window.open(url, target)
        }
      } else if (this.isIOSApp && url === 'rewards') {
        this.postMessageToIOS('iosListener', 'OPEN_SCAN_IMEI')
        this.$router.push({ name: url })
      } else {
        this.$router.push({ name: url })
      }
    },
    handleNotificationClick () {
      this.$router.push(app.routerName.notification)
    },
    handleSettingsClick () {
      let trmsId = null

      if (this.isHomePageRoute) {
        trmsId = 'MIT1003-0002'
      } else if (this.isScanPageRoute) {
        trmsId = 'MIT1007-0021'
      }

      if (trmsId) {
        this.$trackButtonClick(COMPANY_SECTION, {
          cp1: SETTINGS,
          trms_id: trmsId
        })
      }

      this.$router.push(pages.profile)
    },
    formatDate (date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      return `${day}-${month}-${year}`
    },
    handleCopyClipboard (value, toastMsg) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(value).then(() => {
          this.showToast('', toastMsg)
        }).catch((err) => {
          console.log(err)
        })
      } else {
        this.showToast('', this.$t('order.clipboardNotSupported'))
      }
    },
    handleRedirectionUrl (link) {
      if (!link) return

      let finalLink = link
      if (!/^https?:\/\//i.test(finalLink)) {
        finalLink = 'https://' + finalLink
      }

      try {
        const targetUrl = new URL(finalLink)
        const appOrigin = window.location.origin
        const isSameDomain = targetUrl.origin === appOrigin

        if (isSameDomain) {
          window.location.href = targetUrl.href
        } else {
          if (this.isIOSApp) {
            this.postMessageToIOS('openExternalLinkListener', { link: finalLink })
            return
          }

          if (this.isAndroidApp) {
            this.postMessageToAndroid({
              openbrowser: finalLink
            })
            return
          }
          window.open(targetUrl.href, '_blank', 'noopener,noreferrer')
        }
      } catch (error) {
        console.error('[CommonMixin] Invalid redirection link:', finalLink, error)
      }
    },
    viewTermsAndConditions (isRegistrationFlow = false, isProfileFlow = false) {
      const routeConfig = {
        name: app.routerName.termsAndCondition,
        query: {}
      }
      if (isRegistrationFlow) {
        routeConfig.query.type = TNC_TYPES.REGISTRATION
      } else if (isProfileFlow) {
        routeConfig.query.type = TNC_TYPES.PROFILE
      }
      if (this.isMobile || isProfileFlow) {
        this.$router.push(routeConfig)
      } else {
        const tncRoute = this.$router.resolve(routeConfig)
        window.open(tncRoute.href, '_blank')
      }
    }
  }
}
